import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const chipVariants = cva(
  "inline-flex items-center rounded-full border text-xs font-medium transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-border bg-background text-foreground",
        filled: "border-transparent bg-secondary text-secondary-foreground",
        success: "border-success/30 bg-success-muted text-success",
        error: "border-destructive/30 bg-destructive-muted text-destructive",
        warning: "border-warning/30 bg-warning-muted text-warning",
        info: "border-primary/30 bg-primary-muted text-primary-muted-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
      },
      size: {
        default: "px-2.5 py-0.5",
        sm: "px-2 py-0.5 text-[0.7rem]",
        lg: "px-3 py-1",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ChipProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof chipVariants> {
  label?: string
  onDelete?: () => void
}

function Chip({
  className,
  variant,
  size,
  label,
  children,
  onDelete,
  ...props
}: ChipProps) {
  return (
    <div className={cn(chipVariants({ variant, size }), className)} {...props}>
      {label || children}
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="ml-1 rounded-full p-0.5 hover:bg-accent"
        >
          <X className="h-3 w-3" />
          <span className="sr-only">Remove</span>
        </button>
      )}
    </div>
  )
}

export { Chip, chipVariants }
